import { makeAutoObservable, runInAction } from 'mobx';
import { fileApi, type FileRecord } from '../api/file_api';

export interface UploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
  result?: FileRecord;
}

export class FileStore {
  // Files indexed by key
  files: Map<string, FileRecord> = new Map();
  // Files indexed by uuid for quick lookup
  filesByUuid: Map<string, FileRecord> = new Map();

  // Upload tracking
  uploads: Map<string, UploadProgress> = new Map();

  // Loading state
  loading = false;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed getters
  get pendingUploads(): UploadProgress[] {
    return Array.from(this.uploads.values()).filter(
      (u) => u.status === 'pending' || u.status === 'uploading'
    );
  }

  get hasActiveUploads(): boolean {
    return this.pendingUploads.length > 0;
  }

  getFileByKey(key: string): FileRecord | undefined {
    return this.files.get(key);
  }

  getFileByUuid(uuid: string): FileRecord | undefined {
    return this.filesByUuid.get(uuid);
  }

  // Actions
  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch a file by UUID
   */
  async fetchByUuid(uuid: string): Promise<FileRecord | undefined> {
    // Check cache first
    const cached = this.filesByUuid.get(uuid);
    if (cached) return cached;

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const file = await fileApi.getByUuid(uuid);

      runInAction(() => {
        this.files.set(file.key, file);
        this.filesByUuid.set(file.uuid, file);
        this.loading = false;
      });

      return file;
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch file';
      });
      return undefined;
    }
  }

  /**
   * Upload a file
   * Uses user authentication - no identity needed
   */
  async uploadFile(
    file: File,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<FileRecord | undefined> {
    const uploadId = `${file.name}-${Date.now()}`;

    runInAction(() => {
      this.uploads.set(uploadId, {
        file,
        progress: 0,
        status: 'uploading',
      });
      this.error = null;
    });

    try {
      const result = await fileApi.uploadFile(file, disposition);

      runInAction(() => {
        this.files.set(result.key, result);
        this.filesByUuid.set(result.uuid, result);
        this.uploads.set(uploadId, {
          file,
          progress: 100,
          status: 'complete',
          result,
        });
      });

      return result;
    } catch (err) {
      runInAction(() => {
        const errorMessage =
          err instanceof Error ? err.message : 'Upload failed';
        this.uploads.set(uploadId, {
          file,
          progress: 0,
          status: 'error',
          error: errorMessage,
        });
        this.error = errorMessage;
      });
      return undefined;
    }
  }

  /**
   * Upload multiple files
   * Uses user authentication - no identity needed
   */
  async uploadFiles(
    files: File[],
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<FileRecord[]> {
    const results = await Promise.all(
      files.map((file) => this.uploadFile(file, disposition))
    );
    return results.filter((r): r is FileRecord => r !== undefined);
  }

  /**
   * Get download URL for a file
   */
  async getDownloadUrl(
    associationUuid: string,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<string | null> {
    try {
      const result = await fileApi.getDownloadUrl(associationUuid, disposition);
      return result.url;
    } catch {
      return null;
    }
  }

  /**
   * Get thumbnail URL for a file
   */
  async getThumbnailUrl(
    identityKey: string,
    associationUuid: string
  ): Promise<string | null> {
    try {
      const result = await fileApi.getThumbnailUrl(
        identityKey,
        associationUuid,
        'inline'
      );
      return result.url;
    } catch {
      return null;
    }
  }

  /**
   * Clear completed uploads
   */
  clearCompletedUploads(): void {
    runInAction(() => {
      for (const [id, upload] of this.uploads) {
        if (upload.status === 'complete' || upload.status === 'error') {
          this.uploads.delete(id);
        }
      }
    });
  }

  /**
   * Receive realtime update for a file
   */
  receiveFileUpdate(file: FileRecord): void {
    runInAction(() => {
      this.files.set(file.key, file);
      this.filesByUuid.set(file.uuid, file);
    });
  }
}

export const fileStore = new FileStore();
